import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Save } from "lucide-react";
import { useApiMutation } from "@/hooks/useApiMutation";

interface EmailTemplate {
  _id: string;
  type: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
}

export function EmailTemplateTab() {
  
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const { data: template, isLoading } = useQuery<EmailTemplate>({
    queryKey: ["/api/email-templates/team_invitation"],
    queryFn: async () => {
      return apiRequest("GET", "/api/email-templates/team_invitation") as unknown as EmailTemplate;
    },
  });

  useEffect(() => {
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
    }
  }, [template]);

  const updateTemplate = useApiMutation({
    endpoint: `/api/email-templates/${template?._id}`,
    method: "PUT",
    invalidate: [["/api/email-templates/team_invitation"]],
    successMessage: "Email template has been saved successfully.",
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle>Team Invitation Email</CardTitle>
            <CardDescription>
              Customize the email sent when you invite team members
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Subject</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>

        <div>
          <Label>Message Body</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Email body"
            rows={12}
            className="font-mono text-sm"
          />
        </div>

        <div className="bg-muted p-4 rounded-lg">
          <p className="text-sm font-medium mb-2">Available Variables:</p>
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            {template?.variables.map((variable) => (
              <code key={variable} className="bg-background px-2 py-1 rounded">
                {`{{${variable}}}`}
              </code>
            ))}
          </div>
        </div>

        <Button
          onClick={() => updateTemplate.mutate({ subject, body })}
          disabled={updateTemplate.isPending}
          className="w-full"
        >
          <Save className="w-4 h-4 mr-2" />
          {updateTemplate.isPending ? "Saving..." : "Save Template"}
        </Button>
      </CardContent>
    </Card>
  );
}
